<script setup>
import { ref, onMounted } from 'vue'
import { useAuthStore } from '../../store/auth'
import { useContentStore } from '../../store/content'
import api from '../../services/api'
import { useToast } from 'primevue/usetoast'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const contentStore = useContentStore()
const toast = useToast()
const router = useRouter()

const loading = ref(false)
const activeTab = ref(0)

// Document Types Management
const documentTypes = ref([])
const showCreateTypeDialog = ref(false)
const newDocumentType = ref({
  codigo: '',
  nombre: '',
  descripcion: '',
  activo: true,
  extensionesPermitidas: 'pdf',
  tamanioMaximoMb: 10,
  permitirMultiples: false
})

// Users Management
const users = ref([])
const showCreateUserDialog = ref(false)
const newUser = ref({
  firebaseUid: '',
  email: '',
  nombreCompleto: '',
  rol: 'USER'
})

// Load document types
const loadDocumentTypes = async () => {
  loading.value = true
  try {
    const response = await api.getDocumentTypes(false)
    if (response.success) {
      documentTypes.value = response.data
    }
  } catch (error) {
    toast.add({ 
      severity: 'error', 
      summary: 'Error', 
      detail: 'Error al cargar tipos de documento', 
      life: 3000 
    })
  } finally {
    loading.value = false
  }
}

// Load users
const loadUsers = async () => {
  loading.value = true
  try {
    const response = await api.getUsers(false)
    if (response.success) {
      users.value = response.data
    }
  } catch (error) {
    toast.add({ 
      severity: 'error', 
      summary: 'Error', 
      detail: 'Error al cargar usuarios', 
      life: 3000 
    })
  } finally {
    loading.value = false
  }
}

// Create document type
const createDocumentType = async () => {
  loading.value = true
  try {
    const response = await api.createDocumentType(newDocumentType.value)
    if (response.success) {
      toast.add({ 
        severity: 'success', 
        summary: 'Éxito', 
        detail: 'Tipo de documento creado correctamente', 
        life: 3000 
      })
      showCreateTypeDialog.value = false
      resetNewDocumentType()
      await loadDocumentTypes()
      await contentStore.initializeDocumentTypes()
    }
  } catch (error) {
    toast.add({ 
      severity: 'error', 
      summary: 'Error', 
      detail: error.response?.data?.message || 'Error al crear tipo de documento', 
      life: 5000 
    })
  } finally {
    loading.value = false
  }
}

// Create user
const createUser = async () => {
  loading.value = true
  try {
    const response = await api.createUser(newUser.value)
    if (response.success) {
      toast.add({ 
        severity: 'success', 
        summary: 'Éxito', 
        detail: 'Usuario creado correctamente', 
        life: 3000 
      })
      showCreateUserDialog.value = false
      resetNewUser()
      await loadUsers()
    }
  } catch (error) {
    toast.add({ 
      severity: 'error', 
      summary: 'Error', 
      detail: error.response?.data?.message || 'Error al crear usuario', 
      life: 5000 
    })
  } finally {
    loading.value = false
  }
}

// Toggle user active status
const toggleUserStatus = async (user) => {
  loading.value = true
  try {
    if (user.activo) {
      await api.deactivateUser(user.id)
    } else {
      await api.activateUser(user.id)
    }
    toast.add({ 
      severity: 'success', 
      summary: 'Éxito', 
      detail: `Usuario ${user.activo ? 'desactivado' : 'activado'} correctamente`, 
      life: 3000 
    })
    await loadUsers()
  } catch (error) {
    toast.add({ 
      severity: 'error', 
      summary: 'Error', 
      detail: 'Error al cambiar estado del usuario', 
      life: 3000 
    })
  } finally {
    loading.value = false
  }
}

// Initialize all basic document types
const initializeBasicTypes = async () => {
  loading.value = true
  
  const basicTypes = [
    { codigo: 'MISION_VISION', nombre: 'Misión, visión, funciones y deberes', descripcion: 'Información sobre la misión, visión, funciones y deberes de la institución', extensionesPermitidas: 'pdf,docx', tamanioMaximoMb: 10, permitirMultiples: false },
    { codigo: 'ESTRUCTURA_ORGANICA', nombre: 'Estructura orgánica - Organigrama', descripcion: 'Organigrama y estructura organizacional de la institución', extensionesPermitidas: 'pdf,png,jpg', tamanioMaximoMb: 5, permitirMultiples: false },
    { codigo: 'DIRECTORIO_INSTITUCIONAL', nombre: 'Directorio Institucional', descripcion: 'Información de contacto de la institución', extensionesPermitidas: 'pdf,xlsx', tamanioMaximoMb: 5, permitirMultiples: false },
    { codigo: 'LEYES', nombre: 'Leyes', descripcion: 'Leyes aplicables a la institución educativa', extensionesPermitidas: 'pdf', tamanioMaximoMb: 20, permitirMultiples: true },
    { codigo: 'NORMATIVA_APLICABLE', nombre: 'Normativa aplicable', descripcion: 'Normativas aplicables a la institución', extensionesPermitidas: 'pdf,docx', tamanioMaximoMb: 15, permitirMultiples: true },
    { codigo: 'POLITICAS_LINEAMIENTOS', nombre: 'Políticas, lineamientos y manuales', descripcion: 'Políticas institucionales y lineamientos operativos', extensionesPermitidas: 'pdf,docx', tamanioMaximoMb: 20, permitirMultiples: true }
  ]
  
  let created = 0
  let errors = 0
  
  for (const type of basicTypes) {
    try {
      await api.createDocumentType({ ...type, activo: true })
      created++
    } catch (error) {
      errors++
      console.error(`Error creando tipo ${type.codigo}:`, error)
    }
  }
  
  loading.value = false
  
  if (created > 0) {
    toast.add({ 
      severity: 'success', 
      summary: 'Éxito', 
      detail: `${created} tipos de documento creados`, 
      life: 3000 
    })
    await loadDocumentTypes()
    await contentStore.initializeDocumentTypes()
  }
  
  if (errors > 0) {
    toast.add({ 
      severity: 'warn', 
      summary: 'Advertencia', 
      detail: `${errors} tipos no se pudieron crear (pueden existir ya)`, 
      life: 3000 
    })
  }
}

const resetNewDocumentType = () => {
  newDocumentType.value = {
    codigo: '',
    nombre: '',
    descripcion: '',
    activo: true,
    extensionesPermitidas: 'pdf',
    tamanioMaximoMb: 10,
    permitirMultiples: false
  }
}

const resetNewUser = () => {
  newUser.value = {
    firebaseUid: '',
    email: '',
    nombreCompleto: '',
    rol: 'USER'
  }
}

const goToAdmin = () => {
  router.push('/portal-transparencia/admin')
}

onMounted(async () => {
  if (!authStore.isAdmin) {
    toast.add({ 
      severity: 'error', 
      summary: 'Acceso denegado', 
      detail: 'No tiene permisos para acceder a esta página', 
      life: 3000 
    })
    router.push('/portal-transparencia')
    return
  }
  
  await loadDocumentTypes()
  await loadUsers()
})
</script>

<template>
  <div class="setup-container p-4">
    <Toast />
    <div class="flex justify-content-between align-items-center mb-4">
      <h1 class="text-3xl font-bold">Configuración del Sistema</h1>
      <Button label="Ir a Gestión de Documentos" icon="pi pi-arrow-right" @click="goToAdmin" />
    </div>
    
    <TabView v-model:activeIndex="activeTab">
      <!-- Document Types Tab -->
      <TabPanel header="Tipos de Documento">
        <div class="mb-3 flex justify-content-between align-items-center">
          <div>
            <h2 class="text-xl font-semibold mb-2">Gestión de Tipos de Documento</h2>
            <p class="text-color-secondary">Configure los tipos de documento que se pueden subir en cada sección.</p>
          </div>
          <div class="flex gap-2">
            <Button 
              label="Crear Tipos Básicos" 
              icon="pi pi-plus-circle" 
              severity="secondary"
              @click="initializeBasicTypes" 
              :loading="loading"
            />
            <Button 
              label="Nuevo Tipo" 
              icon="pi pi-plus" 
              @click="showCreateTypeDialog = true" 
            />
          </div>
        </div>
        
        <DataTable 
          :value="documentTypes" 
          :loading="loading"
          paginator 
          :rows="10"
          responsiveLayout="scroll"
        >
          <Column field="codigo" header="Código" sortable></Column>
          <Column field="nombre" header="Nombre" sortable></Column>
          <Column field="descripcion" header="Descripción"></Column>
          <Column field="extensionesPermitidas" header="Extensiones"></Column>
          <Column field="tamanioMaximoMb" header="Tamaño Max (MB)"></Column>
          <Column field="permitirMultiples" header="Múltiples">
            <template #body="slotProps">
              <Tag :value="slotProps.data.permitirMultiples ? 'Sí' : 'No'" 
                   :severity="slotProps.data.permitirMultiples ? 'success' : 'info'" />
            </template>
          </Column>
          <Column field="activo" header="Estado">
            <template #body="slotProps">
              <Tag :value="slotProps.data.activo ? 'Activo' : 'Inactivo'" 
                   :severity="slotProps.data.activo ? 'success' : 'danger'" />
            </template>
          </Column>
        </DataTable>
      </TabPanel>
      
      <!-- Users Tab -->
      <TabPanel header="Usuarios Autorizados">
        <div class="mb-3 flex justify-content-between align-items-center">
          <div>
            <h2 class="text-xl font-semibold mb-2">Gestión de Usuarios</h2>
            <p class="text-color-secondary">Administre los usuarios que pueden acceder al sistema.</p>
          </div>
          <Button 
            label="Nuevo Usuario" 
            icon="pi pi-user-plus" 
            @click="showCreateUserDialog = true" 
          />
        </div>
        
        <DataTable 
          :value="users" 
          :loading="loading"
          paginator 
          :rows="10"
          responsiveLayout="scroll"
        >
          <Column field="nombreCompleto" header="Nombre" sortable></Column>
          <Column field="email" header="Email" sortable></Column>
          <Column field="rol" header="Rol">
            <template #body="slotProps">
              <Tag :value="slotProps.data.rol" 
                   :severity="slotProps.data.rol === 'ADMIN' ? 'danger' : 'info'" />
            </template>
          </Column>
          <Column field="activo" header="Estado">
            <template #body="slotProps">
              <Tag :value="slotProps.data.activo ? 'Activo' : 'Inactivo'" 
                   :severity="slotProps.data.activo ? 'success' : 'danger'" />
            </template>
          </Column>
          <Column field="fechaCreacion" header="Fecha Creación">
            <template #body="slotProps">
              {{ new Date(slotProps.data.fechaCreacion).toLocaleDateString() }}
            </template>
          </Column>
          <Column header="Acciones">
            <template #body="slotProps">
              <Button 
                :icon="slotProps.data.activo ? 'pi pi-ban' : 'pi pi-check'" 
                :label="slotProps.data.activo ? 'Desactivar' : 'Activar'"
                :severity="slotProps.data.activo ? 'danger' : 'success'"
                size="small"
                @click="toggleUserStatus(slotProps.data)" 
              />
            </template>
          </Column>
        </DataTable>
      </TabPanel>
      
      <!-- Help Tab -->
      <TabPanel header="Ayuda">
        <div class="help-content">
          <h2 class="text-2xl font-semibold mb-3">Guía de Uso</h2>
          
          <Card class="mb-3">
            <template #title>1. Crear Usuario en Firebase</template>
            <template #content>
              <ol class="pl-4">
                <li class="mb-2">Ve a <a href="https://console.firebase.google.com/" target="_blank" class="text-primary">Firebase Console</a></li>
                <li class="mb-2">Selecciona el proyecto: <strong>tasco-d18f8</strong></li>
                <li class="mb-2">Ve a <strong>Authentication → Users → Add user</strong></li>
                <li class="mb-2">Ingresa email y contraseña</li>
                <li class="mb-2"><strong>Copia el UID del usuario</strong> (lo necesitarás en el siguiente paso)</li>
              </ol>
            </template>
          </Card>
          
          <Card class="mb-3">
            <template #title>2. Autorizar Usuario en el Sistema</template>
            <template #content>
              <ol class="pl-4">
                <li class="mb-2">Ve a la pestaña <strong>"Usuarios Autorizados"</strong></li>
                <li class="mb-2">Haz clic en <strong>"Nuevo Usuario"</strong></li>
                <li class="mb-2">Pega el <strong>UID de Firebase</strong> que copiaste</li>
                <li class="mb-2">Ingresa el email (debe coincidir con Firebase)</li>
                <li class="mb-2">Ingresa el nombre completo</li>
                <li class="mb-2">Selecciona el rol (ADMIN o USER)</li>
                <li class="mb-2">Haz clic en <strong>"Crear"</strong></li>
              </ol>
            </template>
          </Card>
          
          <Card class="mb-3">
            <template #title>3. Crear Tipos de Documento</template>
            <template #content>
              <p class="mb-3">Tienes dos opciones:</p>
              <h4 class="font-semibold mb-2">Opción A: Crear tipos básicos automáticamente</h4>
              <ol class="pl-4 mb-3">
                <li class="mb-2">Ve a la pestaña <strong>"Tipos de Documento"</strong></li>
                <li class="mb-2">Haz clic en <strong>"Crear Tipos Básicos"</strong></li>
                <li class="mb-2">Se crearán automáticamente los tipos más comunes</li>
              </ol>
              
              <h4 class="font-semibold mb-2">Opción B: Crear tipo personalizado</h4>
              <ol class="pl-4">
                <li class="mb-2">Ve a la pestaña <strong>"Tipos de Documento"</strong></li>
                <li class="mb-2">Haz clic en <strong>"Nuevo Tipo"</strong></li>
                <li class="mb-2">Completa el formulario</li>
                <li class="mb-2">Haz clic en <strong>"Crear"</strong></li>
              </ol>
            </template>
          </Card>
          
          <Card>
            <template #title>4. Subir Documentos</template>
            <template #content>
              <ol class="pl-4">
                <li class="mb-2">Haz clic en <strong>"Ir a Gestión de Documentos"</strong> (arriba a la derecha)</li>
                <li class="mb-2">Selecciona una sección del menú lateral</li>
                <li class="mb-2">Haz clic en <strong>"Subir Documento"</strong></li>
                <li class="mb-2">Selecciona tu archivo</li>
                <li class="mb-2">Haz clic en <strong>"Subir"</strong></li>
              </ol>
            </template>
          </Card>
        </div>
      </TabPanel>
    </TabView>
    
    <!-- Create Document Type Dialog -->
    <Dialog 
      v-model:visible="showCreateTypeDialog" 
      header="Crear Tipo de Documento" 
      :style="{ width: '600px' }"
      modal
    >
      <div class="flex flex-column gap-3">
        <div class="field">
          <label for="codigo" class="font-semibold">Código *</label>
          <InputText 
            id="codigo" 
            v-model="newDocumentType.codigo" 
            class="w-full" 
            placeholder="Ej: MISION_VISION"
          />
          <small class="text-color-secondary">Identificador único en mayúsculas con guiones bajos</small>
        </div>
        
        <div class="field">
          <label for="nombre" class="font-semibold">Nombre *</label>
          <InputText 
            id="nombre" 
            v-model="newDocumentType.nombre" 
            class="w-full" 
            placeholder="Ej: Misión, visión, funciones y deberes"
          />
        </div>
        
        <div class="field">
          <label for="descripcion" class="font-semibold">Descripción</label>
          <Textarea 
            id="descripcion" 
            v-model="newDocumentType.descripcion" 
            rows="3" 
            class="w-full"
            placeholder="Descripción del tipo de documento"
          />
        </div>
        
        <div class="field">
          <label for="extensiones" class="font-semibold">Extensiones Permitidas *</label>
          <InputText 
            id="extensiones" 
            v-model="newDocumentType.extensionesPermitidas" 
            class="w-full" 
            placeholder="pdf,docx,xlsx"
          />
          <small class="text-color-secondary">Separadas por comas</small>
        </div>
        
        <div class="field">
          <label for="tamanio" class="font-semibold">Tamaño Máximo (MB) *</label>
          <InputNumber 
            id="tamanio" 
            v-model="newDocumentType.tamanioMaximoMb" 
            class="w-full" 
            :min="1" 
            :max="100"
          />
        </div>
        
        <div class="field-checkbox">
          <Checkbox 
            id="multiples" 
            v-model="newDocumentType.permitirMultiples" 
            :binary="true"
          />
          <label for="multiples" class="ml-2">Permitir múltiples documentos activos</label>
        </div>
      </div>
      
      <template #footer>
        <Button 
          label="Cancelar" 
          icon="pi pi-times" 
          severity="secondary"
          @click="showCreateTypeDialog = false" 
        />
        <Button 
          label="Crear" 
          icon="pi pi-check" 
          @click="createDocumentType" 
          :loading="loading"
        />
      </template>
    </Dialog>
    
    <!-- Create User Dialog -->
    <Dialog 
      v-model:visible="showCreateUserDialog" 
      header="Crear Usuario Autorizado" 
      :style="{ width: '500px' }"
      modal
    >
      <div class="flex flex-column gap-3">
        <Message severity="info" :closable="false">
          Primero debes crear el usuario en Firebase Console y copiar su UID.
        </Message>
        
        <div class="field">
          <label for="firebaseUid" class="font-semibold">Firebase UID *</label>
          <InputText 
            id="firebaseUid" 
            v-model="newUser.firebaseUid" 
            class="w-full" 
            placeholder="Pega el UID de Firebase aquí"
          />
        </div>
        
        <div class="field">
          <label for="userEmail" class="font-semibold">Email *</label>
          <InputText 
            id="userEmail" 
            v-model="newUser.email" 
            type="email"
            class="w-full" 
            placeholder="usuario@colegio.com"
          />
          <small class="text-color-secondary">Debe coincidir con el email en Firebase</small>
        </div>
        
        <div class="field">
          <label for="nombreCompleto" class="font-semibold">Nombre Completo *</label>
          <InputText 
            id="nombreCompleto" 
            v-model="newUser.nombreCompleto" 
            class="w-full" 
            placeholder="Nombre del usuario"
          />
        </div>
        
        <div class="field">
          <label for="rol" class="font-semibold">Rol *</label>
          <Dropdown 
            id="rol" 
            v-model="newUser.rol" 
            :options="['USER', 'ADMIN']" 
            class="w-full"
          />
          <small class="text-color-secondary">ADMIN: puede gestionar todo. USER: solo puede ver</small>
        </div>
      </div>
      
      <template #footer>
        <Button 
          label="Cancelar" 
          icon="pi pi-times" 
          severity="secondary"
          @click="showCreateUserDialog = false" 
        />
        <Button 
          label="Crear" 
          icon="pi pi-check" 
          @click="createUser" 
          :loading="loading"
        />
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.help-content {
  max-width: 800px;
}

.help-content ol {
  list-style-type: decimal;
}

.help-content a {
  text-decoration: underline;
}
</style>
